/**
 * Running track of packing-slip / Cheetah prints per order + delivery day.
 * Orders arrive daily but are packed weekly — operators need to see whether
 * this week's slip was already printed (and how many sheets).
 *
 * Collection: packingSlipPrintCounts
 * Doc id: {sourceId}__{deliveryYmd}
 */

import {
  collection,
  doc,
  getDocs,
  increment,
  onSnapshot,
  query as fsQuery,
  setDoc,
  Timestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { getPrintingOrderDeliveryDay } from '../utils/printingToolkitFilterOptions';

export const PACKING_SLIP_PRINT_COUNTS_COLLECTION = 'packingSlipPrintCounts';

export function getOrderPrintSourceId(order) {
  return String(
    order?.source_id ||
      order?.sourceId ||
      order?.orderNumber ||
      order?.order_number ||
      order?.orderId ||
      order?.id ||
      '',
  )
    .trim()
    .replace(/^#/, '');
}

export function buildPackingSlipPrintCountDocId(sourceId, deliveryYmd) {
  const id = String(sourceId || '')
    .trim()
    .replace(/[/\s]/g, '_');
  const day = String(deliveryYmd || 'unknown').trim() || 'unknown';
  return `${id}__${day}`;
}

/** Printed badge counts print runs, never requested copies or physical sheets. */
export function getPackingSlipPrintRunCount(data) {
  const value = data?.printJobs ?? data?.count ?? data?.sheetCount ?? 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

/**
 * @param {object[]} orders
 * @param {{
 *   copies?: number,
 *   jobType?: string,
 *   userId?: string|null,
 *   physicalSheetsByOrder?: Record<string, number>,
 *   copiesByOrder?: Record<string, number>,
 * }} [opts]
 */
export async function recordPackingSlipPrintCounts(orders, opts = {}) {
  const list = Array.isArray(orders) ? orders : [];
  if (!list.length) return;
  const copies = Math.max(1, parseInt(opts.copies, 10) || 1);
  const jobType = opts.jobType || 'packing_slip';
  const userId = opts.userId || null;
  const batch = writeBatch(db);
  let ops = 0;

  list.forEach((order) => {
    const sourceId = getOrderPrintSourceId(order);
    if (!sourceId) return;
    const pagesForOrder = Math.max(
      1,
      Number(opts.physicalSheetsByOrder?.[sourceId]) || 1,
    );
    const orderCopies = Math.max(
      1,
      parseInt(opts.copiesByOrder?.[sourceId], 10) || copies,
    );
    const sheetIncrement = orderCopies * pagesForOrder;
    const deliveryYmd = getPrintingOrderDeliveryDay(order) || 'unknown';
    const docId = buildPackingSlipPrintCountDocId(sourceId, deliveryYmd);
    const ref = doc(db, PACKING_SLIP_PRINT_COUNTS_COLLECTION, docId);
    batch.set(
      ref,
      {
        sourceId,
        deliveryDate: deliveryYmd,
        count: increment(1),
        sheetCount: increment(sheetIncrement),
        printJobs: increment(1),
        lastPrintedAt: Timestamp.now(),
        lastJobType: jobType,
        lastCopies: orderCopies,
        lastPhysicalPagesPerCopy: pagesForOrder,
        ...(userId ? { lastPrintedBy: userId } : {}),
      },
      { merge: true },
    );
    ops += 1;
  });

  if (ops > 0) await batch.commit();

  try {
    const { recordPackingSlipPrintPreferences } = await import(
      './packingSlipPrintPreferenceService'
    );
    await recordPackingSlipPrintPreferences(list, {
      jobType,
      copies,
      copiesByOrder: opts.copiesByOrder,
      userId,
    });
  } catch (error) {
    console.warn(
      '[packingSlipPrintPreferences] update failed (rules may block):',
      error?.code || error,
    );
  }
}

/**
 * Load counts for a set of source ids (optional delivery-day filter).
 * @returns {Promise<Record<string, number>>} map key `${sourceId}__${deliveryYmd}` -> count
 */
export async function fetchPackingSlipPrintCounts(sourceIds, deliveryYmd = '') {
  const ids = Array.from(new Set((sourceIds || []).map((s) => String(s || '').trim()).filter(Boolean)));
  if (!ids.length) return {};

  const out = {};
  // Firestore 'in' limit 30 — chunk
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    const q = fsQuery(
      collection(db, PACKING_SLIP_PRINT_COUNTS_COLLECTION),
      where('sourceId', 'in', chunk),
    );
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = d.data() || {};
      const day = String(data.deliveryDate || '');
      if (deliveryYmd && day && day !== deliveryYmd) return;
      const key = buildPackingSlipPrintCountDocId(data.sourceId || d.id, day || 'unknown');
      out[key] = getPackingSlipPrintRunCount(data);
    });
  }
  return out;
}

/**
 * Live map of print counts for the given source ids.
 * @param {string[]} sourceIds
 * @param {(map: Record<string, number>) => void} onChange
 * @param {string} [deliveryYmd] when set, only that delivery day
 * @returns {() => void} unsubscribe
 */
export function subscribePackingSlipPrintCounts(sourceIds, onChange, deliveryYmd = '') {
  const ids = Array.from(new Set((sourceIds || []).map((s) => String(s || '').trim()).filter(Boolean)));
  if (!ids.length) {
    onChange({});
    return () => {};
  }

  const unsubs = [];
  const merged = {};

  const publish = () => onChange({ ...merged });

  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    const q = fsQuery(
      collection(db, PACKING_SLIP_PRINT_COUNTS_COLLECTION),
      where('sourceId', 'in', chunk),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        chunk.forEach((sid) => {
          Object.keys(merged).forEach((k) => {
            if (k.startsWith(`${sid}__`)) delete merged[k];
          });
        });
        snap.forEach((d) => {
          const data = d.data() || {};
          const day = String(data.deliveryDate || '');
          if (deliveryYmd && day && day !== deliveryYmd) return;
          const key = buildPackingSlipPrintCountDocId(data.sourceId || '', day || 'unknown');
          merged[key] = getPackingSlipPrintRunCount(data);
        });
        publish();
      },
      (err) => {
        console.warn('[packingSlipPrintCounts] listener failed:', err?.code || err);
      },
    );
    unsubs.push(unsub);
  }

  return () => unsubs.forEach((u) => u());
}

export function lookupPackingSlipPrintCount(countMap, order) {
  const sourceId = getOrderPrintSourceId(order);
  const day = getPrintingOrderDeliveryDay(order) || 'unknown';
  if (!sourceId) return 0;
  const key = buildPackingSlipPrintCountDocId(sourceId, day);
  return Number(countMap?.[key] || 0) || 0;
}
